import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import AssetRegistryPage from './screens/assetRegistry/AssetRegistryPage';
import DisposalReconPage from './features/disposal-recon/DisposalReconPage';
import LifecycleReconPage from './features/recon-hub/pages/LifecycleReconPage';
import DisposalPortal from './features/disposal-portal/DisposalPortal';
import ProtectedRoute from './components/ProtectedRoute';
import ListAssignmentPage from './features/admin/ListAssignmentPage';
import UserManagementPage from './features/admin/UserManagementPage';
import SiteManagementPage from './features/admin/SiteManagementPage';
import DataOpsScreen from './features/admin/DataOpsScreen';
import ProjectsScreen from './features/admin/ProjectsScreen';
import DispositionReportingPage from './features/admin/DispositionReportingPage';
import InventoryScreen from './features/inventory/pages/InventoryScreen';
import ToolListScreen from './features/inventory/pages/ToolListScreen';
import ToolDetailScreen from './features/inventory/pages/ToolDetailScreen';
import DataExchangePage from './features/dataExchange/DataExchangePage';
import DisposalProjectListPage from './pages/disposal/DisposalProjectListPage';
import CreateScrapProjectPage from './pages/disposal/CreateScrapProjectPage';
import ScrapProjectDetailPage from './pages/disposal/ScrapProjectDetailPage';
import BolDetailPage from './pages/disposal/BolDetailPage';
import DigitizationPage from './pages/DigitizationPage';
import ToolMovesPage from './pages/ToolMovesPage';
import WarehouseItemsPage from './features/warehouse/pages/WarehouseItemsPage';
import WarehouseReceivePage from './features/warehouse/pages/WarehouseReceivePage';
import ContractsPage from './features/warehouse/pages/ContractsPage';
import AppShell from './components/AppShell/AppShell';
import SupplierProjectSelectorPage from './pages/supplier/SupplierProjectSelectorPage';
import SupplierShipLogPage from './pages/supplier/SupplierShipLogPage';
import SupplierLoadingPage from './pages/supplier/SupplierLoadingPage';
import SupplierBolPreviewPage from './pages/supplier/SupplierBolPreviewPage';
import SupplierSignaturePage from './pages/supplier/SupplierSignaturePage';
import SupplierRollOffPage from './pages/supplier/SupplierRollOffPage';
import SupplierRollOffBolPage from './pages/supplier/SupplierRollOffBolPage';
import SupplierBolEditPage from './pages/supplier/SupplierBolEditPage';
import MobileLauncher from './screens/launcher/MobileLauncher';
import FieldZonePlaceholder from './features/field/pages/FieldZonePlaceholder';
import FieldProtectedRoute, { useFieldAuth } from './features/field/auth/FieldProtectedRoute';
import PrivacyNoticePage from './pages/legal/PrivacyNoticePage';
import TermsOfUsePage from './pages/legal/TermsOfUsePage';
import AccessibilityPage from './pages/legal/AccessibilityPage';
import SecurityPage from './pages/legal/SecurityPage';
import { UserProvider, useUser } from './shared/context/UserContext';
import { ProjectProvider } from './shared/context/ProjectContext';
import { hasDisposalAccess } from './shared/auth/disposalRoles';
import { hasFieldAccess } from './shared/auth/fieldRoles';
import { useLauncherContext } from './shared/hooks/useLauncherContext';
import './App.css';

/* ── Legacy param redirects ──────────────────────────────────────────────── */

function RedirectWithParams() {
  const location = useLocation();
  const newPath = location.pathname.replace(/^\/disposal\/assets/, '/registry')
    .replace(/^\/assets/, '/registry')
    .replace(/^\/inventory/, '/disposal/inventory');
  return <Navigate to={newPath} replace />;
}

/* ── Admin guard ─────────────────────────────────────────────────────────── */

function AdminRoute({ children }) {
  const { userProfile, isLoading } = useUser();
  if (isLoading) return null;
  if (userProfile?.role !== 'ADMIN') return <Navigate to="/" replace />;
  return children;
}

function SupplierGuard({ children }) {
  const { userProfile, isLoading } = useUser();
  if (isLoading) return null;
  const role = userProfile?.role;
  if (hasDisposalAccess(role)) return children;
  return <Navigate to="/" replace />;
}

function FieldGuard({ children }) {
  const { userProfile, isLoading } = useUser();
  const { offline, networkDown } = useFieldAuth();
  // Offline: FieldProtectedRoute already role-gated off snapshot.identity.role,
  // and userProfile is unreachable without the network — don't bounce.
  if (offline) return children;
  // F-29: account present, network down (State 1b). Reaching this line means
  // `offline` is false, which means FieldProtectedRoute took its State 1 arm,
  // which means an MSAL ACCOUNT EXISTS — so this is exactly the
  // "networkDown AND authenticated" case, established by position rather than
  // by re-deriving hasAccount here.
  //
  // MSAL has already authenticated this rep. The role gate below is
  // UNSATISFIABLE offline: UserContext nulls userProfile on ANY fetch failure
  // (UserContext.jsx:109) and never persists one, and isLoading still reaches
  // false via its .finally — so hasFieldAccess(undefined) returns false and the
  // rep is evicted to "/", where DefaultRedirect renders null for the same
  // missing profile. Denying on an unanswerable question is the bug. Defer.
  if (networkDown) return children;
  if (isLoading) return null;
  if (hasFieldAccess(userProfile?.role)) return children;
  return <Navigate to="/" replace />;
}

function DefaultRedirect() {
  const { userProfile, isLoading } = useUser();
  const isLauncherContext = useLauncherContext();
  if (isLoading || !userProfile) return null;
  if (isLauncherContext) return <Navigate to="/home" replace />;
  return <Navigate to="/registry" replace />;
}

/* ── App content ─────────────────────────────────────────────────────────── */

function AppContent() {
  const location = useLocation();
  const { profileStatus, profileErrorStatus, retryProfile } = useUser();
  const isLegalRoute = location.pathname.startsWith('/legal/');
  const isSupplierRoute = location.pathname.startsWith('/supplier/');
  const isHomeRoute = location.pathname === '/home';
  // Unlike /supplier/* (whose routes all live under /supplier/disposal), the
  // field zone's entry route IS the zone root, so match '/field' exactly too.
  const isFieldRoute = location.pathname === '/field' || location.pathname.startsWith('/field/');

  // Launcher (Jitterbug Phase 2a) renders without AppShell — direct-URL only,
  // no auto-redirect today. Mirrors the supplier-route chrome-bypass pattern.
  if (isHomeRoute) {
    return (
      <ProtectedRoute>
        <Routes>
          <Route path="/home" element={<MobileLauncher />} />
        </Routes>
      </ProtectedRoute>
    );
  }

  // Legal pages render without authentication and without AppShell
  if (isLegalRoute) {
    return (
      <Routes>
        <Route path="/legal/privacy" element={<PrivacyNoticePage />} />
        <Route path="/legal/terms" element={<TermsOfUsePage />} />
        <Route path="/legal/accessibility" element={<AccessibilityPage />} />
        <Route path="/legal/security" element={<SecurityPage />} />
        <Route path="*" element={<Navigate to="/legal/privacy" replace />} />
      </Routes>
    );
  }

  // F-13: the field zone must be reachable before the profile-status
  // terminal gates below. An offline cold start fails the profile fetch
  // (UserContext.jsx:114 -> profileStatus 'authError', status 0), which
  // returns a routerless panel and strands a rep who has a valid device
  // snapshot. Safe above those gates because FieldProtectedRoute enforces
  // account-or-snapshot, hasFieldAccess off the snapshot, and the
  // navigator.onLine split independently of profileStatus.
  // Ordered AFTER /home and /legal/* deliberately.
  // Field capture zone (Inventory Screens Step 4.1/4.2b) renders without
  // AppShell — mirrors the /supplier/* chrome-bypass pattern. Guarded by the
  // field-zone FieldProtectedRoute (MSAL online / tmg-field snapshot offline),
  // NOT the app-wide ProtectedRoute — the offline walls exist only here.
  // FIELD_REP/ADMIN only; everything unknown inside the zone falls back to /field.
  if (isFieldRoute) {
    return (
      <FieldProtectedRoute>
        <Routes>
          <Route path="/field" element={
            <FieldGuard><FieldZonePlaceholder /></FieldGuard>
          } />
          <Route path="*" element={<Navigate to="/field" replace />} />
        </Routes>
      </FieldProtectedRoute>
    );
  }

  // Terminal bootstrap states — ONE place, deliberately positioned:
  // AFTER the /legal/* early-return so the public pages stay reachable by a
  // visitor with no account, and BEFORE every authenticated branch so no guard
  // can miss it. NOT a redirect on either state — the redirect loop was the bug
  // (a null profile bounced through DefaultRedirect and the guards forever).
  // 'anonymous' and 'ready' fall through; 'loading' also falls through and keeps
  // the existing loading render (ProtectedRoute's, and DefaultRedirect's null).
  if (profileStatus === 'unprovisioned') {
    return (
      <div className="ts-terminal-page" data-testid="profile-unprovisioned">
        <div className="ts-terminal-card">
          <h1 className="ts-terminal-headline">
            We couldn&apos;t confirm your ToolSight access.
          </h1>
          <p className="ts-terminal-body">Contact your TMG administrator.</p>
          <p className="ts-terminal-ref" data-testid="profile-unprovisioned-ref">
            Reference: {profileErrorStatus}
          </p>
        </div>
      </div>
    );
  }

  if (profileStatus === 'authError') {
    // 401 is its own arm INSIDE this panel — deliberately not a new
    // profileStatus. A 401 means the token was rejected against the Users
    // container, which an administrator has to repair; every other outcome
    // that lands here (5xx, 429, a rejected fetch) is transient and Retry
    // alone may clear it. Same panel, same gate, different copy.
    const isUnmatchedSignIn = profileErrorStatus === 401;
    // profileErrorStatus is 0 when no response reached us at all
    // (UserContext.jsx:113 — network failure, CORS rejection, abort).
    // "Reference: 0" tells a support call nothing; NETWORK does.
    const reference = profileErrorStatus === 0 ? 'NETWORK' : profileErrorStatus;
    return (
      <div className="ts-terminal-page" data-testid="profile-auth-error">
        <div className="ts-terminal-card">
          <h1 className="ts-terminal-headline">We couldn&apos;t verify your sign-in.</h1>
          {isUnmatchedSignIn ? (
            <p className="ts-terminal-body">
              We can&apos;t match your sign-in to a ToolSight account. A TMG
              administrator needs to correct your account record. Once they
              confirm it&apos;s done, press Retry below — you do not need to
              sign out or sign in again.
            </p>
          ) : (
            <p className="ts-terminal-body">
              Something went wrong while checking your access. Press Retry
              below. If it keeps failing, contact a TMG administrator.
            </p>
          )}
          <p className="ts-terminal-ref" data-testid="profile-auth-error-ref">
            Reference: {reference}
          </p>
          <button
            type="button"
            className="ts-terminal-btn"
            data-testid="profile-retry-btn"
            onClick={retryProfile}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Supplier routes render without AppShell — full-screen SupplierLayout
  if (isSupplierRoute) {
    return (
      <ProtectedRoute>
        <Routes>
          <Route path="/supplier/disposal" element={
            <SupplierGuard><SupplierProjectSelectorPage /></SupplierGuard>
          } />
          <Route path="/supplier/disposal/:projectId/log" element={
            <SupplierGuard><SupplierShipLogPage /></SupplierGuard>
          } />
          <Route path="/supplier/disposal/:projectId/load" element={
            <SupplierGuard><SupplierLoadingPage /></SupplierGuard>
          } />
          <Route path="/supplier/disposal/:projectId/load/:bolId" element={
            <SupplierGuard><SupplierLoadingPage /></SupplierGuard>
          } />
          <Route path="/supplier/disposal/:projectId/bol/:bolId/preview" element={
            <SupplierGuard><SupplierBolPreviewPage /></SupplierGuard>
          } />
          <Route path="/supplier/disposal/:projectId/bol/:bolId/edit" element={
            <SupplierGuard><SupplierBolEditPage /></SupplierGuard>
          } />
          <Route path="/supplier/disposal/:projectId/bol/:bolId/sign" element={
            <SupplierGuard><SupplierSignaturePage /></SupplierGuard>
          } />
          <Route path="/supplier/disposal/:projectId/rolloff" element={
            <SupplierGuard><SupplierRollOffPage /></SupplierGuard>
          } />
          <Route path="/supplier/disposal/:projectId/rolloff/:bolId" element={
            <SupplierGuard><SupplierRollOffPage /></SupplierGuard>
          } />
          <Route path="/supplier/disposal/:projectId/rolloff/:bolId/pickup" element={
            <SupplierGuard><SupplierRollOffBolPage /></SupplierGuard>
          } />
          <Route path="*" element={<Navigate to="/supplier/disposal" replace />} />
        </Routes>
      </ProtectedRoute>
    );
  }

  // All other routes use AppShell
  return (
    <ProtectedRoute>
      <AppShell>
        <Routes>
          {/* Default landing */}
          <Route path="/" element={<DefaultRedirect />} />

          {/* ── Asset Registry ────────────────────────────────── */}
          <Route path="/registry" element={<AssetRegistryPage />} />

          {/* ── Placeholder modules (role-branched inside the page) ─ */}
          <Route path="/digitization" element={<DigitizationPage />} />
          <Route path="/moves" element={<ToolMovesPage />} />

          {/* ── Digitization sub-routes (mirror of the disposal/inventory subtree) ─ */}
          <Route path="/digitization/registry" element={<AssetRegistryPage />} />
          <Route path="/digitization/inventory" element={<InventoryScreen mode="disposal" sectionLabel="Digitization" />} />
          <Route path="/digitization/inventory/:siteId" element={<ToolListScreen />} />
          <Route path="/digitization/inventory/:siteId/new" element={<ToolDetailScreen />} />
          <Route path="/digitization/inventory/:siteId/:toolId" element={<ToolDetailScreen />} />

          {/* ── Disposal branch ───────────────────────────────── */}
          <Route path="/disposal" element={<Navigate to="/disposal/recon" replace />} />
          <Route path="/disposal/recon" element={<DisposalReconPage />} />
          <Route path="/disposal/portal" element={<DisposalPortal />} />
          <Route path="/disposal/inventory" element={<InventoryScreen mode="disposal" />} />
          <Route path="/disposal/inventory/:siteId" element={<ToolListScreen />} />
          <Route path="/disposal/inventory/:siteId/new" element={<ToolDetailScreen />} />
          <Route path="/disposal/inventory/:siteId/:toolId" element={<ToolDetailScreen />} />

          {/* ── Warehouse branch (read scaffold, Phase 3a) ─────── */}
          <Route path="/warehouse" element={<Navigate to="/warehouse/items" replace />} />
          <Route path="/warehouse/items" element={<WarehouseItemsPage />} />
          <Route path="/warehouse/receive" element={<WarehouseReceivePage />} />
          <Route path="/warehouse/contracts" element={<ContractsPage />} />

          {/* ── Lifecycle branch ──────────────────────────────── */}
          <Route path="/lifecycle" element={<Navigate to="/lifecycle/recon" replace />} />
          <Route path="/lifecycle/recon" element={<LifecycleReconPage />} />
          <Route path="/lifecycle/inventory" element={<InventoryScreen mode="lifecycle" />} />
          <Route path="/lifecycle/inventory/:siteId" element={<ToolListScreen />} />
          <Route path="/lifecycle/inventory/:siteId/new" element={<ToolDetailScreen />} />
          <Route path="/lifecycle/inventory/:siteId/:toolId" element={<ToolDetailScreen />} />

          {/* ── Admin branch ──────────────────────────────────── */}
          <Route path="/admin" element={<AdminRoute><Navigate to="/admin/list-assignment" replace /></AdminRoute>} />
          <Route path="/admin/list-assignment" element={<AdminRoute><ListAssignmentPage /></AdminRoute>} />
          <Route path="/admin/user-management" element={<AdminRoute><UserManagementPage /></AdminRoute>} />
          <Route path="/admin/site-management" element={<AdminRoute><SiteManagementPage /></AdminRoute>} />
          <Route path="/admin/data-ops" element={<AdminRoute><DataOpsScreen /></AdminRoute>} />
          <Route path="/admin/projects" element={<AdminRoute><ProjectsScreen /></AdminRoute>} />
          <Route path="/admin/data" element={<AdminRoute><DataExchangePage /></AdminRoute>} />
          <Route path="/admin/disposal" element={<AdminRoute><DisposalProjectListPage /></AdminRoute>} />
          <Route path="/admin/disposal/new" element={<AdminRoute><CreateScrapProjectPage /></AdminRoute>} />
          <Route path="/admin/disposal/:projectId" element={<AdminRoute><ScrapProjectDetailPage /></AdminRoute>} />
          <Route path="/admin/disposal/:projectId/bol/:bolId" element={<AdminRoute><BolDetailPage /></AdminRoute>} />
          <Route path="/admin/dispositions" element={<AdminRoute><DispositionReportingPage /></AdminRoute>} />

          {/* ── Legacy redirects ──────────────────────────────── */}
          <Route path="/assets" element={<Navigate to="/registry" replace />} />
          <Route path="/disposal/assets" element={<Navigate to="/registry" replace />} />
          <Route path="/disposal/assets/:id" element={<RedirectWithParams />} />
          <Route path="/assets/:id" element={<RedirectWithParams />} />
          <Route path="/recon/disposal" element={<Navigate to="/disposal/recon" replace />} />
          <Route path="/recon/lifecycle" element={<Navigate to="/lifecycle/recon" replace />} />
          <Route path="/inventory" element={<Navigate to="/disposal/inventory" replace />} />
          <Route path="/inventory/*" element={<RedirectWithParams />} />

          {/* Catch-all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AppShell>
    </ProtectedRoute>
  );
}

function App() {
  return (
    <BrowserRouter>
      <UserProvider>
        <ProjectProvider>
          <AppContent />
        </ProjectProvider>
      </UserProvider>
    </BrowserRouter>
  );
}

export default App;
